import { Component, inject, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Annotation, MarkdownAnnotation, NullAnnotation, WordAnnotation, WordPlusMarkdownAnnotation } from '@models/Annotation';
import { MarkdownPipe } from '../../markdown.pipe'; // Adjust the path as necessary
import { SynchronizerService } from '../../synchronizer.service';
import { ProjectTextStyleDirective } from '../../project-text-style.directive';
import { AnnotationType } from '@models/AnnotationJsonObject';
import { Gloss } from '@models/Gloss';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../confirmation-dialog/confirmation-dialog.component';

export interface GlossDialogInput {
  type: AnnotationType;
  newGloss: boolean;
  gloss?: Gloss;
}

export type GlossDialogOutput =
  | { action: 'edit' | 'delete'; gloss: Gloss }
  | { action: 'create'; annotation: Annotation };

@Component({
  selector: 'app-gloss-dialog',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MarkdownPipe,
    ProjectTextStyleDirective,
    MatIconModule
  ],
  templateUrl: './gloss-dialog.component.html',
  styleUrl: './gloss-dialog.component.scss'
})
export class GlossDialogComponent {

  readonly dialogRef = inject(MatDialogRef<GlossDialogComponent, GlossDialogOutput>);
  readonly data = inject<GlossDialogInput>(MAT_DIALOG_DATA);
  readonly dialog = inject(MatDialog);

  readonly gloss = model('');
  readonly markdown = model('');
  private id = -1;

  constructor(public synchronizerService: SynchronizerService) {
    if (this.data.gloss) {
      const obj = this.data.gloss.annotation.toAnnotationObject().content;
      if (typeof obj === 'object' && 'gloss' in obj) {
        this.gloss.set(obj.gloss);
      }
      if (typeof obj === 'object' && 'markdown' in obj) {
        this.markdown.set(obj.markdown);
      }
      this.id = this.data.gloss.annotation.gloss_id;
    }
  }

  onDismiss(): void {
    this.dialogRef.close();
  }

  accept(): void {
    let a: Annotation;
    switch (this.data.type) {
      case 'word':
        a = new WordAnnotation(this.gloss(), this.id, this.data.gloss?.voice || 'NA');
        break;
      case 'markdown':
        a = new MarkdownAnnotation(this.markdown(), this.id, this.data.gloss?.voice || 'NA');
        break;
      case 'wordplusmarkdown':
        a = new WordPlusMarkdownAnnotation(this.gloss(), this.markdown(), this.id, this.data.gloss?.voice || 'NA');
        break;
      case 'null':
        a = new NullAnnotation(this.id);
        break;
    }
    if (this.data.newGloss) {
      const result: GlossDialogOutput = { action: 'create', annotation: a };
      this.dialogRef.close(result);
    } else if (this.data.gloss) {
      const result: GlossDialogOutput = { action: 'edit', gloss: this.data.gloss.replaceAnnotation(a) };
      this.dialogRef.close(result);
    }
  }

  delete() {
    const data: ConfirmationDialogData = {
      title: `Confirm deletion`,
      message: `Are you sure you want to delete the gloss <b>${this.gloss()} ${this.markdown()}</b>? This will delete it everywhere in the project, not just in this verse. This action cannot be undone.`,
      yes: 'Delete Gloss',
      no: 'Dismiss',
      challengeString: 'DELETE'
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: data
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.dialogRef.close({ action: 'delete', gloss: this.data.gloss! });
      }
    });

  }

}
